import { Component, Input } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Permission } from 'src/app/classes/model/permissions';
import { DigitalAccessName, Student, UnlimitedDigitalAccess } from 'src/app/classes/model/student';
import { ConfirmationDialogService } from 'src/app/modules/confirmation-dialog/services/confirmation-dialog.service';
import { NotifierService } from 'src/app/services/common/notifier-service.service';
import { PermissionService } from 'src/app/services/common/permission.service';
import { StudentService } from 'src/app/services/student.service';

type UnlimitedAccessOption = {
  accessName:DigitalAccessName;
  label:string;
};

/**
 * Lejárat nélküli hozzáférés adása és elvétele modulonként (pl. oktatóknak a termék bemutatásához).
 * Független a csomagok és az előfizetés által adott, időkorlátos hozzáférésektől.
 */
@Component({
  selector: 'app-student-unlimited-access',
  templateUrl: './student-unlimited-access.component.html',
  styleUrls: ['./student-unlimited-access.component.scss']
})
export class StudentUnlimitedAccessComponent {
  @Input({ required: true }) student:Student;

  protected readonly accessOptions:ReadonlyArray<UnlimitedAccessOption> = [
    { accessName: "theory_module_access", label: "Elméleti" },
    { accessName: "practical_module_access", label: "Forgalmi" }
  ];

  protected readonly canModify:boolean;
  protected isSaving:boolean = false;

  constructor(
    private studentService:StudentService,
    private confirmationDialogService:ConfirmationDialogService,
    private notifierService:NotifierService,
    permissionService:PermissionService
  ) {
    this.canModify = permissionService.isLoggedUserHasPermission(Permission.StudentGiftWrite);
  }

  protected getUnlimitedAccess(accessName:DigitalAccessName):UnlimitedDigitalAccess | undefined {
    return this.student.unlimitedDigitalAccesses?.[accessName];
  }

  protected hasUnlimitedAccess(accessName:DigitalAccessName):boolean {
    return this.getUnlimitedAccess(accessName) != null;
  }

  protected onToggleChange(option:UnlimitedAccessOption, change:MatSlideToggleChange):void {
    // A kapcsoló csak megerősítés és sikeres mentés után vált át (a tanuló adatából számolódik)
    change.source.checked = !change.checked;

    const isUnlimited:boolean = change.checked;
    const label:string = option.label.toLowerCase();

    this.confirmationDialogService.open(
      isUnlimited ? `Korlátlan ${label} hozzáférés adása` : `Korlátlan ${label} hozzáférés elvétele`,
      isUnlimited
        ? `Biztosan korlátlan, lejárat nélküli ${label} hozzáférést adsz a tanulónak? A csomagjait és az előfizetését ez nem érinti.`
        : `Biztosan elveszed a korlátlan ${label} hozzáférést? A csomagjaiból és az előfizetéséből származó hozzáférése megmarad.`,
      () => this.saveUnlimitedAccess(option, isUnlimited),
      undefined,
      isUnlimited ? "Megadás" : "Elvétel",
      "Mégse"
    );
  }

  private async saveUnlimitedAccess(option:UnlimitedAccessOption, isUnlimited:boolean):Promise<void> {
    this.isSaving = true;
    try {
      const isSuccessful:boolean = await this.studentService.setUnlimitedDigitalAccess(this.student, option.accessName, isUnlimited);
      if(isSuccessful) {
        this.notifierService.notify("success", `Korlátlan ${option.label.toLowerCase()} hozzáférés ${isUnlimited ? "megadva" : "elvéve"}.`);
      }
    } finally {
      this.isSaving = false;
    }
  }
}
